import { SVGS } from '@/assets/svgs'
import {
  Input,
  InteractionComment,
  ModernComment,
  ModernCommentInput,
  ModernCommentsHeader,
  ModernCommentsEmptyState,
} from '@/components'
import {
  useDeleteComment,
  useGetDMTopicDetails,
  useGetTopicDetails,
  useGetTopicNoteLikes,
  useInteractToNote,
  userAddNoteComment,
  useNoteLoad,
} from '@/hooks/api/dashboard'
import { useAuth, useBrowserQuery, useLocalStorage } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import colors from '@/styles/colors'
import { Comment, Ego, NoteDetail } from '@/types'
import { ArrowBackIos, ChevronLeft, ChevronRight, Close, Favorite, Send } from '@mui/icons-material'
import { Box, Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { shallow } from 'zustand/shallow'
import { useLocation, useNavigate } from 'react-router-dom'
import { getNowInUTC, scrollToBottom } from '@/utils/helper'
import SVG from 'react-inlinesvg'
import { styled } from '@mui/material/styles'

// Clean Discord-style layout
const ModernCommentsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: '#ffffff',
})

const CommentsScrollArea = styled(Box)({
  flex: 1,
  padding: '16px 16px 8px 16px',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#ffffff',
})

const InputSection = styled(Box)({
  backgroundColor: '#ffffff',
})

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px',
})

interface Props {
  trackKey: string
}

export default function NoteComments({ trackKey }: Props) {
  const [noteData, setNoteData] = useState<NoteDetail | null>(null)
  const [isViewMode, setIsViewMode] = useState(false)
  const [newComment, setNewComment] = useState('')

  const [ewUser] = useLocalStorage('ewUser', null)
  const { basicInfo } = useAuth()

  const query = useBrowserQuery()
  const topicID = query.get(trackKey) || ''
  const noteID = query.get('noteID') || ''

  const noteLoad = useNoteLoad(noteID)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const addNoteComment = userAddNoteComment()
  const deleteComment = useDeleteComment(topicID, noteID)
  const interactToNote = useInteractToNote()
  const [topicSelectedAt] = useAppStore((state) => [state.home.topicSelectedAt], shallow)
  const [comments, setComments] = useState<Comment[]>([])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sliderRef = useRef<any>(null)
  const listRef = useRef<any>(null)

  useEffect(() => {
    if (noteID) noteLoad.mutate()
  }, [topicSelectedAt])

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(0)
    }
  }, [sliderRef.current, noteID])

  useEffect(() => {
    if (noteID) {
      setIsViewMode(false)
      setNewComment('')
      setNoteData(null)
      noteLoad.mutate()
    }
  }, [noteID])

  useEffect(() => {
    if (noteLoad.isSuccess) {
      setNoteData(noteLoad.data)
      setComments(noteLoad.data.comments)
      scrollToBottom(listRef, 10)
    }
  }, [noteLoad.isSuccess])

  useEffect(() => {
    if (addNoteComment.isSuccess && !addNoteComment.data?.isError) {
      setNewComment('')
      const { comment: commentText, commentID } = addNoteComment.data
      const comment: Comment = {
        comment: commentText,
        dateAdded: getNowInUTC(),
        feedbackID: commentID,
        noteID,
        topicID,
        senderName: `${basicInfo.firstName} ${basicInfo.lastName}`,
        senderID: ewUser.userID,
        userID: ewUser.userID,
        profilePicURL: basicInfo.profilePicThumbURL,
      }
      const updated = [...comments, comment]
      setComments(updated)
      scrollToBottom(listRef, 10)
    }
  }, [addNoteComment.isSuccess])

  if (noteLoad.isError) {
    return <Typography>Failed to load</Typography>
  }

  //if (!noteData || !noteID) return null

  const onClickDelete = (comment: Comment) => {
    let filtered = comments.filter((e) => e.feedbackID != comment.feedbackID)
    setComments(filtered)
    toast.promise(deleteComment.mutateAsync(comment.feedbackID), {
      loading: 'Deleting a comment...',
      success: <Typography variant='p'>Successfully deleted</Typography>,
      error: <Typography variant='p'>Failed to delete</Typography>,
    })
  }

  const onChangeNewComment = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewComment(e.target.value)
  }

  const onClickSendNewComment = () => {
    if (newComment.trim()) {
      addNoteComment.mutate({ topicID, noteID, comment: newComment })
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      onClickSendNewComment()
    }
  }

  const handleBack = () => {
    query.delete('noteID')
    query.delete('step')
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const renderComments = () => {
    if (!noteData || noteLoad.isLoading) {
      return (
        <LoadingContainer>
          <CircularProgress size={40} />
        </LoadingContainer>
      )
    }

    if (comments.length === 0) {
      return (
        <ModernCommentsEmptyState
          title='No comments yet'
          subtitle='Be the first to share your thoughts on this design!'
          showSuggestions={true}
          onStartConversation={() => {
            // Focus on input - we'll implement this if needed
          }}
        />
      )
    }

    return (
      <>
        {comments.map((comment) => (
          <ModernComment
            key={comment.feedbackID}
            comment={comment}
            onClickDelete={() => onClickDelete(comment)}
            deletable={
              ewUser?.userID && comment.userID === ewUser.userID && !deleteComment.isLoading
            }
          />
        ))}
      </>
    )
  }

  return (
    <ModernCommentsContainer>
      <ModernCommentsHeader
        commentsCount={comments.length || 0}
        onBack={handleBack}
        title='Comments'
        subtitle='Share your thoughts and feedback'
        showCounts={true}
      />

      <CommentsScrollArea ref={listRef}>{renderComments()}</CommentsScrollArea>

      {ewUser && (
        <InputSection>
          <ModernCommentInput
            value={newComment}
            placeholder='Add a comment...'
            disabled={addNoteComment.isLoading}
            isLoading={addNoteComment.isLoading}
            maxLength={500}
            onChange={onChangeNewComment}
            onSubmit={onClickSendNewComment}
            onKeyDown={handleKeyDown}
          />
        </InputSection>
      )}
      {!ewUser && (
        <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #e5e7eb' }}>
          <Typography variant='body2' color='text.secondary'>
            Please{' '}
            <Box
              component='span'
              onClick={() => navigate('/auth?step=login')}
              sx={{
                fontWeight: 600,
                color: '#6366f1',
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(99, 102, 241, 0.3)',
                textUnderlineOffset: '2px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  color: '#4f46e5',
                  textDecorationColor: 'rgba(79, 70, 229, 0.6)',
                },
              }}
            >
              sign in
            </Box>{' '}
            to add a comment
          </Typography>
        </Box>
      )}
    </ModernCommentsContainer>
  )
}
